import { Directive, HostListener } from '@angular/core';

@Directive({
  selector: '[appNegativePositiveNumber]'
})
export class NegativePositiveNumberDirective {

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const inputValue = input.value;
    const key = event.key;
    const rawSelectionStart = input.selectionStart;
    const rawSelectionEnd = input.selectionEnd;

    // Allow: control/navigation keys
    if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;

    // Allow: digits 0-9
    if (/[0-9]/.test(key)) return;

    // Allow: a single minus sign only at the beginning.
    if (key === '-') {
      const hasMinus = inputValue.includes('-');

      // If the existing minus is part of the current selection, replacement is allowed.
      const selectedText = inputValue.slice(rawSelectionStart ?? 0, rawSelectionEnd ?? 0);
      const selectedHasMinus = selectedText.includes('-');
      if (hasMinus && !selectedHasMinus) {
        event.preventDefault();
        return;
      }

      // For type="number", caret position may be unavailable (null).
      // In that case, prepend minus so users can turn 1 into -1 after typing.
      if (inputValue.length > 0 && (rawSelectionStart === 0 || (rawSelectionStart === null && input.type === 'number'))) {
        const unsignedValue = inputValue.replace(/-/g, '');
        input.value = `-${unsignedValue}`;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        event.preventDefault();
        return;
      }

      // Empty input should allow entering '-' as first character.
      if (!inputValue) return;

      // Any other minus insertion position is invalid.
      event.preventDefault();
      return;
    }

    // Block everything else
    event.preventDefault();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value ?? '';

    if (!value) return;

    // Keep digits only, and keep minus only if it is the first character.
    const hasLeadingMinus = value.startsWith('-');
    const digits = value.replace(/[^0-9]/g, '');
    const sanitizedValue = (hasLeadingMinus ? '-' : '') + digits;

    if (value !== sanitizedValue) {
      input.value = sanitizedValue;
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const pastedValue = event.clipboardData?.getData('text') ?? '';

    // Allow only valid integers (positive or negative)
    if (!/^-?[0-9]+$/.test(pastedValue)) {
      event.preventDefault();
    }
  }
}